import EditableField from '../EditableField';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import ImagePreview from '../../ImagePreview';
import { BankAttachmentUpload } from '../BankAttachmentUpload';
import { StatusTimeline } from '../StatusTimeline';
import { Pencil, X } from 'lucide-react';
import { formatMoneyFull } from '../../../utils/bankConstants';

const ProfileTab = ({ bank, editData, updateField, canEdit, editingField, editingValue, setEditingValue, startEditing, confirmEdit, cancelEdit }) => {
  const editProps = { canEdit, editingField, editingValue, setEditingValue, startEditing, confirmEdit, cancelEdit };

  return (
    <div className="space-y-4">
      <Card className="p-2 divide-y">
        <EditableField field="bankName" label="Bank Name" value={editData.bankName} {...editProps} />
        <EditableField field="companyName" label="Company Name" value={editData.companyName} {...editProps} />
        <EditableField field="region" label="Region" value={editData.region} {...editProps} />
        <EditableField field="holderName" label="Account Holder" value={editData.holderName} {...editProps} />
        <EditableField field="accountNumber" label="Account Number" value={editData.accountNumber} {...editProps} />
        <EditableField field="ifsc" label="IFSC Code" value={editData.ifsc} {...editProps} />
        <EditableField field="upi" label="UPI ID" value={editData.upi} {...editProps} />
        <EditableField field="bankStartDate" label="Bank Start Date" value={editData.bankStartDate} type="date" {...editProps} />
        <EditableField field="notes" label="Notes / Remarks" value={editData.notes} {...editProps} />
        {/* Account Type */}
        <div className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-slate-50 group">
          <div className="flex-1">
            <span className="text-xs text-slate-500 block">Account Type</span>
            {editingField === 'accountType' ? (
              <div className="flex items-center gap-2 mt-1">
                <Select value={editingValue || 'Savings'} onValueChange={(val) => { updateField('accountType', val); cancelEdit(); }}>
                  <SelectTrigger data-testid="edit-input-accountType" className="h-8 w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Savings">Savings</SelectItem>
                    <SelectItem value="Current">Current</SelectItem>
                    <SelectItem value="Salary">Salary</SelectItem>
                    <SelectItem value="Company Current">Company Current</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={cancelEdit}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <span className="text-sm font-medium">{editData.accountType || 'Savings'}</span>
            )}
          </div>
          {editingField !== 'accountType' && canEdit && (
            <Button data-testid="edit-btn-accountType" variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => startEditing('accountType', editData.accountType)}>
              <Pencil className="w-3 h-3 text-blue-500" />
            </Button>
          )}
        </div>
        {/* Bank Condition */}
        <div className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-slate-50 group">
          <div className="flex-1">
            <span className="text-xs text-slate-500 block">Bank Condition</span>
            {editingField === 'bankCondition' ? (
              <div className="flex items-center gap-2 mt-1">
                <Select value={editingValue || 'Fresh'} onValueChange={(val) => { updateField('bankCondition', val); cancelEdit(); }}>
                  <SelectTrigger data-testid="edit-input-bankCondition" className="h-8 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Fresh">Fresh Bank</SelectItem><SelectItem value="Used">Used Bank</SelectItem></SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={cancelEdit}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <Badge className={editData.bankCondition === 'Used' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>{editData.bankCondition || 'Fresh'} Bank</Badge>
            )}
          </div>
          {editingField !== 'bankCondition' && canEdit && (
            <Button data-testid="edit-btn-bankCondition" variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => startEditing('bankCondition', editData.bankCondition)}>
              <Pencil className="w-3 h-3 text-blue-500" />
            </Button>
          )}
        </div>
      </Card>

      {/* Banking App Checklist */}
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold text-sm text-slate-700">Banking App Availability</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: 'mobileBanking', label: 'Mobile Banking' },
            { key: 'netBanking', label: 'Net Banking' },
            { key: 'phonePeAvailable', label: 'PhonePe' },
            { key: 'googlePayAvailable', label: 'Google Pay' },
            { key: 'paytmAvailable', label: 'Paytm' },
          ].map(item => (
            <div key={item.key} className="flex items-center gap-2 py-1.5 px-3 rounded-md hover:bg-slate-50 group">
              <Checkbox
                id={`detail-${item.key}`}
                checked={!!editData[item.key]}
                onCheckedChange={(checked) => { if (canEdit) updateField(item.key, !!checked); }}
                disabled={!canEdit}
                data-testid={`detail-${item.key}`}
              />
              <label htmlFor={`detail-${item.key}`} className="text-sm cursor-pointer">{item.label}</label>
            </div>
          ))}
        </div>
      </Card>

      {/* Freeze Summary */}
      {bank.status?.startsWith('Freezed') && (
        <Card className="p-4 space-y-3 border-red-200">
          <h3 className="font-semibold text-sm text-red-700">Freeze Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><span className="text-slate-600">Amount:</span><span className="font-bold text-red-600 ml-2">{formatMoneyFull(bank.freezedAmount)}</span></div>
            <div><span className="text-slate-600">Reason:</span><span className="ml-2">{bank.freezeReason || '-'}</span></div>
            <div><span className="text-slate-600">Date:</span><span className="ml-2">{bank.freezeDate || '-'}</span></div>
          </div>
        </Card>
      )}

      {/* Opening Documents */}
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold text-sm text-blue-700">Opening Documents</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Opening Balance Screenshot</Label>
            <div className="mt-2"><ImagePreview imageUrl={editData.openingScreenshotDataUrl} label="Opening Balance" /></div>
            <BankAttachmentUpload field="openingScreenshot" className="mt-2" data-testid="detail-opening-balance-upload" />
          </div>
          <div>
            <Label>Opening Statement</Label>
            <div className="mt-2"><ImagePreview imageUrl={editData.openingStatementDataUrl} label="Opening Statement" /></div>
            <BankAttachmentUpload field="openingStatement" accept="image/*,.pdf" className="mt-2" data-testid="detail-opening-statement-upload" />
          </div>
        </div>
      </Card>

      <StatusTimeline statusHistory={bank.statusHistory} />
    </div>
  );
};

export default ProfileTab;
